// Custom bet (bet builder) helpers shared by the match detail, odd buttons, bet builder modal and bet slip.
//
// Our market string ids only carry specifier values ("18-m-S:2.5"); MyOdds needs the full
// specifier ("total=2.5"). The full specifier is taken from the matching line of
// /api/BetBuilder/available-selections, the same way the server validates custom bets.

export const BET_BUILDER_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  OK: 'ok',
  NOT_COMBINABLE: 'notCombinable',
  RATE_LIMITED: 'rateLimited',
  UNAVAILABLE: 'unavailable'
};

const SPECIFIER_SEPARATOR = '-S:';

const sortedValues = (values) => [...values].sort();

// "18-m-S:2.5" -> ["2.5"], "1-m" -> []
const stringIdValues = (marketStringId) =>
  sortedValues((marketStringId ?? '').split(SPECIFIER_SEPARATOR).slice(1));

// "total=2.5" -> ["2.5"], "mapnr=1|total=10.5" -> ["1", "10.5"]; tolerates legacy JSON quoting
const specifierValues = (specifiers) => {
  const plain = (specifiers ?? '').replace(/^"|"$/g, '');
  if (!plain) return [];
  return sortedValues(plain.split('|').map((kv) => kv.slice(kv.indexOf('=') + 1)));
};

const sameValues = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

const findOfferedLine = (marketId, marketStringId, offeredMarkets) => {
  if (!Array.isArray(offeredMarkets)) return undefined;
  const wanted = stringIdValues(marketStringId);
  return offeredMarkets.find(
    (m) => m.marketId === marketId && sameValues(specifierValues(m.specifiers), wanted)
  );
};

/** MyOdds market for one of our market lines, or null when MyOdds does not offer that line. */
export const resolveMyOddsMarket = (marketId, marketStringId, offeredMarkets) => {
  const line = findOfferedLine(marketId, marketStringId, offeredMarkets);
  if (!line) return null;
  return { market_id: marketId, specifiers: (line.specifiers ?? '').replace(/^"|"$/g, '') };
};

/** Whether a market line (e.g. "Total 2.5") can be used in a custom bet. */
export const isMarketLineOfferedForBetBuilder = (market, offeredMarkets) =>
  !!findOfferedLine(market?.id, market?.stringId, offeredMarkets);

/** Body for /api/BetBuilder/calculate(-filter); null when a selection cannot be resolved. */
export const buildBetBuilderRequest = (selections, offeredMarkets) => {
  const events = {};
  for (const selection of selections) {
    const market = resolveMyOddsMarket(
      selection.marketId,
      selection.marketStringId,
      offeredMarkets
    );
    if (!market) return null;

    if (!events[selection.providerId]) {
      events[selection.providerId] = { id: selection.providerId, market: [] };
    }
    events[selection.providerId].market.push({
      ...market,
      outcome_id: String(selection.outcomeId)
    });
  }
  return { selection: Object.values(events) };
};

/**
 * Whether adding this outcome would make the current selection impossible.
 * filteredOutcomes come from calculate-filter as "marketId|specifiers|outcomeId".
 */
export const isOutcomeConflicting = (
  filteredOutcomes,
  marketId,
  marketStringId,
  outcomeId,
  offeredMarkets
) => {
  if (!filteredOutcomes || filteredOutcomes.length === 0) return false;
  const market = resolveMyOddsMarket(marketId, marketStringId, offeredMarkets);
  if (!market) return false;
  return filteredOutcomes.includes(`${market.market_id}|${market.specifiers}|${outcomeId}`);
};

/** Bet builder status for a failed calculate request (axios error). */
export const betBuilderStatusFromError = (error) => {
  const status = error?.response?.status;
  if (status === 422 || status === 400) return BET_BUILDER_STATUS.NOT_COMBINABLE;
  if (status === 429) return BET_BUILDER_STATUS.RATE_LIMITED;
  return BET_BUILDER_STATUS.UNAVAILABLE;
};

/** Number of bets on the slip: a custom bet group counts once, however many selections it holds. */
export const countSlipEntries = (bets) => {
  const groups = new Set();
  let plain = 0;
  (bets ?? []).forEach((b) => {
    if (b.isBetBuilder) groups.add(b.betBuilderId);
    else plain += 1;
  });
  return plain + groups.size;
};
